/**
 * Typed(-ish) API client for the Provision card (Story 56425).
 *
 * The ONLY module in the feature that calls fetch/axios (claude-code-UI-prompt.md golden
 * rule #2, mirroring professionalContactsApi.js) — every screen goes through here, so the base
 * URL can move from the Prism mock to a real API with one env var.
 *
 * @see contracts/provision-card.contract.yaml (SINGLE SOURCE OF TRUTH) — the contract is law.
 */
import { httpGet, httpPost, httpPut, httpDelete } from "./apis";
// DCF files go direct to the Document Helper, not through our API — same precedent (and the same
// two helpers) as professionalContactsApi.js's uploadQuoteDocument/downloadQuoteDocument. These
// already speak the app's one `api/document_helper` Azure Function contract (Content-Type,
// file-operation/process-file/x-functions-key/subdomain headers) that every other Loan Document
// upload/download in this app goes through. Unlike the calls below they carry their own bearer
// token, bypass apis.js's shared axios config, and reject with a real Error — so they need the
// oidc user passed in and no request()/toApiError() wrapping.
import {
  uploadDocument as uploadToDocumentHelper,
  downloadDocument as downloadFromDocumentHelper,
} from "../../components/Factory/Pages/Common/DocumentsDashboard/DocumentsDashboard.helper";
import { DCF_DOCUMENT_CATEGORY_NAME } from "../../domain/provision/provision.types";
import { buildDcfDocumentFilePath, fileExtensionOf } from "../../domain/provision/provision.rules";

/**
 * The Provision endpoints sit under a VERSIONED path on the shared portal API: `API_URL` ends in
 * `/api`, and these routes are `/api/v1/...`. The version segment is appended here rather than
 * carried in the env files, so this feature adds no env var of its own — one `API_URL` per
 * environment, the same as every other feature in the app. Pointing the card at a different host
 * (a local API, or the Prism mock) is therefore just a matter of changing `API_URL`.
 */
const API_VERSION = "v1";

/** A trailing slash on API_URL would double up against the leading slash on every path below. */
const trimTrailingSlash = (value) => (value || "").replace(/\/+$/, "");

const BASE_URL = `${trimTrailingSlash(process.env.API_URL)}/${API_VERSION}`;

const url = (path) => `${BASE_URL}${path}`;

const toQueryString = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/** apis.js never rejects — failed calls resolve with the axios error object instead. Normalise that here. */
const toApiError = (result) => {
  const looksLikeFailure = result instanceof Error || (result && result.isAxiosError);
  if (!looksLikeFailure) return null;
  const body = result.response && result.response.data;
  const error = new Error((body && body.message) || result.message || "Request failed");
  error.status = result.response && result.response.status;
  error.code = body && body.code;
  error.field = body && body.field;
  return error;
};

/** @param {Promise<any>} pending */
async function request(pending) {
  const result = await pending;
  const error = toApiError(result);
  if (error) throw error;
  return result;
}

// ---------------------------------------------------------------------------------------
// Provision Details / Professionals / Notes (DFXX1-4, DFXX12-19)
// ---------------------------------------------------------------------------------------

/** @param {string} loanId @returns {Promise<import("../../domain/provision/provision.types").Provision>} */
export function getProvision(loanId) {
  return request(httpGet(url(`/loans/${loanId}/provision`)));
}

/**
 * @param {string} loanId
 * @param {import("../../domain/provision/provision.types").ProvisionUpsert} payload
 * @returns {Promise<import("../../domain/provision/provision.types").Provision>}
 */
export function upsertProvision(loanId, payload) {
  return request(httpPut(url(`/loans/${loanId}/provision`), payload));
}

// ---------------------------------------------------------------------------------------
// Scenarios (DFXX9, DFXX10) — own endpoints, mutate immediately (not part of the Save above)
// ---------------------------------------------------------------------------------------

/**
 * @param {string} loanId
 * @returns {Promise<{scenarios: import("../../domain/provision/provision.types").Scenario[], provisionOutcomePct: ?number}>}
 */
export function listScenarios(loanId) {
  return request(httpGet(url(`/loans/${loanId}/provision/scenarios`)));
}

/**
 * @param {string} loanId
 * @param {import("../../domain/provision/provision.types").ScenarioInput} payload
 * @returns {Promise<import("../../domain/provision/provision.types").Scenario>}
 */
export function addScenario(loanId, payload) {
  return request(httpPost(url(`/loans/${loanId}/provision/scenarios`), payload));
}

/**
 * @param {string} loanId
 * @param {string} scenarioId
 * @param {import("../../domain/provision/provision.types").ScenarioInput} payload
 * @returns {Promise<import("../../domain/provision/provision.types").Scenario>}
 */
export function updateScenario(loanId, scenarioId, payload) {
  return request(httpPut(url(`/loans/${loanId}/provision/scenarios/${scenarioId}`), payload));
}

/**
 * @param {string} loanId
 * @param {string} scenarioId
 */
export function deleteScenario(loanId, scenarioId) {
  return request(httpDelete(url(`/loans/${loanId}/provision/scenarios/${scenarioId}`)));
}

// ---------------------------------------------------------------------------------------
// DCF documents (DFXX11) — own endpoints, mutate immediately
// ---------------------------------------------------------------------------------------

/** @param {string} loanId @returns {Promise<import("../../domain/provision/provision.types").DcfDocument[]>} */
export function listDcfDocuments(loanId) {
  return request(httpGet(url(`/loans/${loanId}/provision/dcf-documents`)));
}

/**
 * Two steps, mirroring Professional Contacts' uploadQuoteDocument exactly:
 *
 * 1. The file goes STRAIGHT to the Document Helper Azure Function — the same Loan Documents
 *    storage every other document in this app uses — filed under the fixed, system-generated
 *    `dcf-documents` category, so it appears under Loan Documents with no separate sync step.
 * 2. Only the resulting reference is POSTed to our API as JSON. The bytes never touch it, which
 *    is why validateDcfFile's type/size checks are the real enforcement (see provision.rules.js).
 *
 * The stored path is computed client-side rather than read from the Document Helper's response
 * body — the same convention DocumentsDashboard.jsx and buildQuoteDocumentFilePath rely on.
 *
 * @param {string} loanId
 * @param {File} file
 * @param {{access_token: string}} user the oidc user (state.oidc.user) — the Document Helper
 *   carries its own bearer token and doesn't go through apis.js's shared axios config, so the
 *   caller has to pass this explicitly.
 * @returns {Promise<import("../../domain/provision/provision.types").DcfDocument>}
 */
export async function uploadDcfDocument(loanId, file, user) {
  await uploadToDocumentHelper(file, file.name, DCF_DOCUMENT_CATEGORY_NAME, loanId, user);
  /** @type {import("../../domain/provision/provision.types").DcfDocumentInput} */
  const payload = {
    fileName: file.name,
    filePath: buildDcfDocumentFilePath(loanId, file.name),
    fileExtension: fileExtensionOf(file.name),
    contentType: file.type || null,
    sizeBytes: file.size ?? null,
  };
  return request(httpPost(url(`/loans/${loanId}/provision/dcf-documents`), payload));
}

/**
 * @param {string} loanId
 * @param {string} documentId
 */
export function deleteDcfDocument(loanId, documentId) {
  return request(httpDelete(url(`/loans/${loanId}/provision/dcf-documents/${documentId}`)));
}

/**
 * Opens/downloads a DCF file via the Document Helper directly — our API is NOT a download proxy
 * and holds no bytes, so there is no `.../content` endpoint to call (same as Quote documents).
 *
 * `dcf-documents` is the category segment of the stored path (document.filePath), NOT the
 * Document Helper `type` header: that stays "generic", which is a container discriminator rather
 * than a per-category value — confirmed against DocumentsDashboard.helper.js.
 *
 * Note this RESOLVES having already triggered the browser save itself (downloadDocument builds
 * and clicks the link); it does not return a Blob for the caller to handle.
 *
 * @param {import("../../domain/provision/provision.types").DcfDocument} document
 * @param {{access_token: string}} user
 * @returns {Promise<void>}
 */
export function downloadDcfDocument(document, user) {
  return downloadFromDocumentHelper(document.filePath, user, "generic");
}

// ---------------------------------------------------------------------------------------
// Professionals Repository (DFXX13-19)
// ---------------------------------------------------------------------------------------

/**
 * Type-ahead search backing the Lawyer/Valuer/Surveyor pickers and the 4th-party Company search.
 * @param {"lawyer"|"valuer"|"surveyor"|"company"} type
 * @param {string} [q]
 * @returns {Promise<import("../../domain/provision/provision.types").ProfessionalSummary[]>}
 */
export function searchProfessionals(type, q) {
  return request(httpGet(url(`/professionals${toQueryString({ type, q })}`)));
}

/**
 * Full contact detail for a professional — powers the hover popover (OQ2).
 * @param {string} professionalId
 * @returns {Promise<import("../../domain/provision/provision.types").ProfessionalDetail>}
 */
export function getProfessional(professionalId) {
  return request(httpGet(url(`/professionals/${professionalId}`)));
}

/**
 * Manually adds a new company to the central repository (DFXX17) — used when the 4th-party
 * Company search finds no match.
 * @param {import("../../domain/provision/provision.types").CompanyInput} payload
 * @returns {Promise<import("../../domain/provision/provision.types").ProfessionalSummary>}
 */
export function createCompany(payload) {
  return request(httpPost(url("/companies"), payload));
}

/**
 * The contacts held against a company (DFXX18, DFXX19) — backs the 4th-party Contact dropdown,
 * and is what resolves a saved `fourthParty.contactId` back to a name/number when the card is
 * re-opened. The contract returns the main contact first and flags it with `isMainContact`, which
 * is what the dropdown pre-selects (see defaultFourthPartyContactId in provision.rules.js).
 * @param {string} companyId
 * @returns {Promise<import("../../domain/provision/provision.types").ContactDetail[]>}
 */
export function listCompanyContacts(companyId) {
  return request(httpGet(url(`/companies/${companyId}/contacts`)));
}

/**
 * Manually adds a new contact under a company (DFXX18, DFXX19) — used when the 4th-party Contact
 * dropdown has no match. Per the contract's MAIN-CONTACT RULE the server never makes this the
 * company's main contact (unless it is the company's very first), so `isMainContact` is not sent
 * here — it comes back on the response.
 * @param {string} companyId
 * @param {import("../../domain/provision/provision.types").ContactInput} payload
 * @returns {Promise<import("../../domain/provision/provision.types").ContactDetail>}
 */
export function createContact(companyId, payload) {
  return request(httpPost(url(`/companies/${companyId}/contacts`), payload));
}
